import i18n, { detectBrowserLanguage } from "../i18n";
import type { SupportedLanguage } from "../i18n/resources";
import type { BloodPressureThresholds, EffectiveTheme, Preferences } from "./types";

const storageKey = "health-tracker.preferences.v1";
export const defaultBloodPressureThresholds: BloodPressureThresholds = { systolic: 135, diastolic: 85 };
const defaults: Preferences = { language: "system", theme: "system", weightUnit: "kg", bloodPressureThresholds: defaultBloodPressureThresholds };

export function loadPreferences(): Preferences {
  try {
    const value = window.localStorage.getItem(storageKey);
    if (!value) return defaults;
    const parsed = JSON.parse(value) as Partial<Preferences>;
    return {
      language: parsed.language === "en" || parsed.language === "hr" || parsed.language === "system" ? parsed.language : defaults.language,
      theme: parsed.theme === "light" || parsed.theme === "dark" || parsed.theme === "system" ? parsed.theme : defaults.theme,
      weightUnit: parsed.weightUnit === "lb" ? "lb" : "kg",
      bloodPressureThresholds: migrateBloodPressureThresholds(parsed.bloodPressureThresholds),
    };
  } catch {
    return defaults;
  }
}

export function migrateBloodPressureThresholds(value: unknown): BloodPressureThresholds {
  if (!isObject(value)) return defaultBloodPressureThresholds;
  if (validThreshold(value.systolic, 80, 250) && validThreshold(value.diastolic, 40, 150)) {
    return { systolic: value.systolic, diastolic: value.diastolic };
  }
  if (validThreshold(value.homeSystolic, 80, 250) && validThreshold(value.homeDiastolic, 40, 150)) {
    return { systolic: value.homeSystolic, diastolic: value.homeDiastolic };
  }
  return defaultBloodPressureThresholds;
}

function validThreshold(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= minimum && value <= maximum;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function savePreferences(preferences: Preferences): void {
  window.localStorage.setItem(storageKey, JSON.stringify(preferences));
}

export function resolveLanguage(preferences: Preferences): SupportedLanguage {
  return preferences.language === "system" ? detectBrowserLanguage() : preferences.language;
}

export function resolveTheme(preferences: Preferences): EffectiveTheme {
  if (preferences.theme !== "system") return preferences.theme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function applyPreferences(preferences: Preferences): { language: SupportedLanguage; theme: EffectiveTheme } {
  const language = resolveLanguage(preferences);
  const theme = resolveTheme(preferences);
  document.documentElement.lang = language;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#10131a" : "#f7f8fa");
  if (i18n.resolvedLanguage !== language) void i18n.changeLanguage(language);
  return { language, theme };
}

export function preferenceStorageKey(): string {
  return storageKey;
}
